/**
 * Утилиты актуализации AI (инструкции + база знаний), перенесены с classic Actualization.js.
 */

export const ACT_TYPE_LABELS: Record<string, string> = {
  replace_instructions: 'Замена в инструкциях',
  add_instructions: 'Добавление в инструкции',
  replace_kb: 'Замена в базе знаний',
  add_kb: 'Добавление в базу знаний',
}

export const ACT_FIELD_LABELS: Record<string, string> = {
  category: 'Категория',
  triggers: 'Триггеры',
  action: 'Действие',
  response_from_db: 'Ответ',
  when_transfer_to_operator: 'Когда переводить оператору',
  message_to_client_on_transfer: 'Сообщение клиенту при переводе',
  title: 'Заголовок',
  content_type: 'Тип контента',
  content: 'Контент',
  question: 'Вопрос',
  answer: 'Ответ',
  is_active: 'Активно',
}

const FIELD_SETS = {
  instructions: [
    'category',
    'triggers',
    'action',
    'response_from_db',
    'when_transfer_to_operator',
    'message_to_client_on_transfer',
    'is_active',
  ],
  kb: ['title', 'content_type', 'question', 'answer', 'content', 'is_active'],
} as const

export type ActItem = {
  existing_id?: string
  existing_snapshot?: Record<string, unknown>
  proposed_patch?: Record<string, unknown>
  draft?: Record<string, unknown>
}

export type ActNormalized = {
  replacements: { instructions: ActItem[]; kb: ActItem[] }
  additions: { instructions: ActItem[]; kb: ActItem[] }
  summary_counts?: Record<string, number>
  warnings?: string[]
  quality_report?: ActQualityReport | null
}

export type ActQualityReport = {
  confidence_threshold?: number
  total_items?: number
  kept_items?: number
  skipped_items?: number
  items?: {
    decision?: string
    status?: string
    confidence?: number | null
    existing_id?: string
    preview?: string
    reason?: string
  }[]
}

export type ActMasterItem = {
  key: string
  type: string
  idx: number
  label: string
  preview: string
}

export function isLongActField(key: string): boolean {
  return [
    'triggers',
    'action',
    'response_from_db',
    'when_transfer_to_operator',
    'message_to_client_on_transfer',
    'content',
    'question',
    'answer',
  ].includes(key)
}

function fieldsForType(type: string): readonly string[] {
  return type.includes('instructions') ? FIELD_SETS.instructions : FIELD_SETS.kb
}

export function fieldsForActItem(type: string, item: ActItem): string[] {
  if (!type.includes('kb')) return [...fieldsForType(type)]
  const snap = item?.existing_snapshot || {}
  const changed = item?.proposed_patch || item?.draft || {}
  const contentType = String(
    (changed as { content_type?: string }).content_type ??
      (snap as { content_type?: string }).content_type ??
      '',
  ).toLowerCase()
  if (contentType === 'qa') {
    return ['title', 'content_type', 'question', 'answer', 'is_active']
  }
  return ['title', 'content_type', 'content', 'is_active']
}

function normCmp(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value.replace(/\s+/g, ' ').trim().toLowerCase()
  return String(value)
}

function hasUsefulValue(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (typeof v === 'string') return v.trim() !== ''
  return true
}

function hasAnyUsefulField(obj: Record<string, unknown> | undefined, fields: readonly string[]): boolean {
  if (!obj || typeof obj !== 'object') return false
  return fields.some((k) => hasUsefulValue(obj[k]))
}

export function normalizeActualizationUi(raw: Record<string, unknown> | null | undefined): ActNormalized {
  const data = structuredClone(raw || {}) as Record<string, unknown>
  const r = (data.replacements as { instructions?: ActItem[]; kb?: ActItem[] }) || {}
  const a = (data.additions as { instructions?: ActItem[]; kb?: ActItem[] }) || {}
  const instrFields = FIELD_SETS.instructions
  const kbFields = FIELD_SETS.kb

  const filterReplace = (item: ActItem, fields: readonly string[]) => {
    const snap = item?.existing_snapshot || {}
    const patch = item?.proposed_patch || {}
    return hasAnyUsefulField(snap as Record<string, unknown>, fields) || hasAnyUsefulField(patch as Record<string, unknown>, fields)
  }
  const filterAdd = (item: ActItem, fields: readonly string[]) => {
    const draft = item?.draft || {}
    return hasAnyUsefulField(draft as Record<string, unknown>, fields)
  }

  const replacements = {
    instructions: (r.instructions || []).filter((x) => filterReplace(x, instrFields)),
    kb: (r.kb || []).filter((x) => filterReplace(x, kbFields)),
  }
  const additions = {
    instructions: (a.instructions || []).filter((x) => filterAdd(x, instrFields)),
    kb: (a.kb || []).filter((x) => filterAdd(x, kbFields)),
  }

  const summary_counts = {
    replace_instructions: replacements.instructions.length,
    add_instructions: additions.instructions.length,
    replace_kb: replacements.kb.length,
    add_kb: additions.kb.length,
    total_changes:
      replacements.instructions.length +
      additions.instructions.length +
      replacements.kb.length +
      additions.kb.length,
  }

  return {
    replacements,
    additions,
    summary_counts,
    warnings: Array.isArray(data.warnings) ? (data.warnings as string[]).filter(Boolean) : [],
    quality_report: (data.quality_report as ActQualityReport) || null,
  }
}

function getItemPreview(type: string, item: ActItem): string {
  const src = (item?.proposed_patch || item?.draft || item?.existing_snapshot || {}) as Record<string, unknown>
  const candidates = type.includes('instructions')
    ? [src.triggers, src.action, src.response_from_db, src.category]
    : [src.title, src.question, src.answer, src.content]
  const text = candidates.find((x) => typeof x === 'string' && x.trim()) || 'Без описания'
  return String(text).trim().slice(0, 120)
}

export function buildActMasterItems(data: ActNormalized): ActMasterItem[] {
  const r = data.replacements
  const a = data.additions
  const groups: [string, ActItem[]][] = [
    ['replace_instructions', r.instructions || []],
    ['add_instructions', a.instructions || []],
    ['replace_kb', r.kb || []],
    ['add_kb', a.kb || []],
  ]
  const items: ActMasterItem[] = []
  for (const [type, arr] of groups) {
    arr.forEach((item, idx) => {
      items.push({
        key: `${type}-${idx}`,
        type,
        idx,
        label: ACT_TYPE_LABELS[type] || type,
        preview: getItemPreview(type, item),
      })
    })
  }
  return items
}

/** Поля для отображения в строке сравнения */
export function effectiveFieldsForRow(type: string, item: ActItem): string[] {
  const existing = item?.existing_snapshot || {}
  const changed = item?.proposed_patch || item?.draft || {}
  const allFields = fieldsForActItem(type, item)
  const fields = type.startsWith('replace_')
    ? allFields.filter((k) => {
        if (Object.prototype.hasOwnProperty.call(changed, k)) return true
        const changedVal = (changed as Record<string, unknown>)[k]
        if (changedVal === undefined || changedVal === null || changedVal === '') return false
        return normCmp(changedVal) !== normCmp((existing as Record<string, unknown>)[k])
      })
    : allFields
  return fields.length ? fields : allFields
}

export function getEditableValue(
  type: string,
  item: ActItem,
  key: string,
): unknown {
  const existing = item?.existing_snapshot || {}
  const changed = item?.proposed_patch || item?.draft || {}
  if (type.startsWith('replace_')) {
    let value = (changed as Record<string, unknown>)[key]
    if ((value === undefined || value === null || value === '') && key === 'content_type') {
      value = (existing as Record<string, unknown>)[key]
    }
    if ((value === undefined || value === null) && key === 'is_active') value = true
    return value
  }
  let value = (changed as Record<string, unknown>)[key]
  if ((value === undefined || value === null) && key === 'is_active') value = true
  return value
}

export function updateActItemField(
  pkg: ActNormalized,
  type: string,
  idx: number,
  key: string,
  value: unknown,
): ActNormalized {
  const next = structuredClone(pkg)
  const isReplace = type.startsWith('replace_')
  const isInstr = type.includes('instructions')
  const bucket = isReplace ? 'replacements' : 'additions'
  const cat = isInstr ? 'instructions' : 'kb'
  const arr = next[bucket][cat]
  const item = arr[idx]
  if (!item) return pkg
  const mode = isReplace ? 'proposed_patch' : 'draft'
  if (!item[mode]) item[mode] = {}
  ;(item[mode] as Record<string, unknown>)[key] = value
  return next
}

/** Собрать diff только для отмеченных пунктов (улучшение над classic: без галочки — не уходит в apply). */
export function buildDiffPackageForApply(pkg: ActNormalized, included: Set<string>): Record<string, unknown> {
  const take = (type: string, arr: ActItem[]) =>
    arr
      .map((item, i) => ({ item, i }))
      .filter(({ i }) => included.has(`${type}-${i}`))
      .map(({ item }) => structuredClone(item))

  return {
    summary_counts: pkg.summary_counts,
    warnings: pkg.warnings || [],
    replacements: {
      instructions: take('replace_instructions', pkg.replacements.instructions),
      kb: take('replace_kb', pkg.replacements.kb),
    },
    additions: {
      instructions: take('add_instructions', pkg.additions.instructions),
      kb: take('add_kb', pkg.additions.kb),
    },
  }
}
